/**
 * Base Telemetry Service
 * Adapted from v1 to work with Remix v2 architecture
 * Handles API calls to both legacy telemetry API and new API v2
 */

export default class BaseTelemetryService {
  private telemetryApiBaseUrl: string;
  private apiV2BaseUrl: string;
  private headers: HeadersInit;

  constructor() {
    // Legacy telemetry API (from v1)
    this.telemetryApiBaseUrl =
      process.env.REMIX_APP_TELEMETRY_API_HOST ||
      process.env.TELEMETRY_API_HOST ||
      ""; // Will use API v2 if not set

    // New API v2 (primary)
    this.apiV2BaseUrl =
      process.env.REMIX_APP_API_HOST_V2 ||
      "https://apiv2dev.office-hub.com";

    this.headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  /**
   * GET request to legacy telemetry API
   */
  protected async apiGet(path: string): Promise<any> {
    // Only make API calls in production
    if (process.env.NODE_ENV !== "production") {
      return Promise.resolve(null);
    }

    if (!this.telemetryApiBaseUrl) {
      // If no legacy API configured, skip
      return Promise.resolve(null);
    }

    const url = `${this.telemetryApiBaseUrl}${path}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: this.headers,
      });

      if (!response.ok) {
        console.warn("Telemetry API GET failed:", response.statusText);
        return null;
      }

      return response.json();
    } catch (error) {
      console.warn("Telemetry API GET error:", error);
      return null;
    }
  }

  /**
   * POST request to legacy telemetry API
   */
  protected async apiPost(path: string, data?: any): Promise<any> {
    // Only make API calls in production
    if (process.env.NODE_ENV !== "production") {
      return Promise.resolve(null);
    }

    if (!this.telemetryApiBaseUrl) {
      // If no legacy API configured, skip
      return Promise.resolve(null);
    }

    const url = `${this.telemetryApiBaseUrl}${path}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.headers,
        body: data ? JSON.stringify({ body: data }) : undefined,
      });

      if (!response.ok) {
        console.warn("Telemetry API POST failed:", response.statusText);
        return null;
      }

      return response.json();
    } catch (error) {
      console.warn("Telemetry API POST error:", error);
      return null;
    }
  }

  /**
   * POST request to API v2 (new telemetry endpoints)
   */
  protected async apiPostV2(path: string, data?: any): Promise<any> {
    // Only make API calls in production
    if (process.env.NODE_ENV !== "production") {
      return Promise.resolve(null);
    }

    const url = `${this.apiV2BaseUrl}${path}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.headers,
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        console.warn("Telemetry API v2 POST failed:", response.statusText);
        return null;
      }

      return response.json();
    } catch (error) {
      console.warn("Telemetry API v2 POST error:", error);
      return null;
    }
  }
}

